import React from 'react';
import * as XLSX from 'xlsx';
import { exportReportPdf } from '../reportPdf';

/**
 * The download controls above a report table.
 *
 * Every report used to carry a single button labelled "Download", which gave
 * no clue what came out of it (a .xlsx). There are now two, each saying what
 * it produces, and both export the columns the table shows rather than the
 * raw response - so the file reads the same as the screen.
 *
 * @param {string}   title     the report name, printed on the PDF
 * @param {string[]} subtitle  context lines for the PDF - firm, date range
 * @param {object[]} columns   [{ key, label, align, wrap, format }]
 * @param {object[]} rows      the rows on show
 * @param {string}   fileName  without an extension
 * @param {Function} onExcel    optional - use the page's own spreadsheet export
 * @param {string}   className optional - `rptExportEnd` right-aligns the pair
 */
const ReportExport = ({
    title,
    subtitle = [],
    columns = [],
    rows = [],
    fileName = 'report',
    onExcel,
    className = '',
}) => {
    if (!rows || rows.length === 0) {
        return null;
    }

    const cellValue = (row, column) =>
        column.format ? column.format(row[column.key], row) : row[column.key];

    const handleExcel = () => {
        if (onExcel) {
            onExcel();
            return;
        }

        const sheet = rows.map((row) => {
            const line = {};

            columns.forEach((column) => {
                line[column.label] = cellValue(row, column);
            });

            return line;
        });

        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(sheet), 'Report');
        XLSX.writeFile(book, fileName + '.xlsx');
    };

    const handlePdf = () => {
        exportReportPdf({ title, subtitle, columns, rows, fileName });
    };

    return (
        <div className={'rptExport ' + className}>
            <button
                type="button"
                className="rptExportBtn rptExportExcel"
                onClick={handleExcel}
                title="Save this report as an Excel spreadsheet (.xlsx) you can sort and total"
            >
                Download Excel
            </button>

            <button
                type="button"
                className="rptExportBtn rptExportPdf"
                onClick={handlePdf}
                title="Save this report as a landscape PDF, ready to print or e-mail"
            >
                Download PDF
            </button>
        </div>
    );
};

export default ReportExport;
